import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  DialogContentText,
  SxProps,
  Grid,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getGridRoles, getRoleById, removeRole } from "../api/requests/roles";
import { getUserForRole } from "../api/requests/users";
import { useAlert } from "../context/AlertContext";
import CommDataGrid, {
  titleDescriptionStyles,
  titleStyles,
} from "../components/CommDataGrid";
import CreateRoleModal from "../components/CreateRoleModal";
import CommonDialog from "../components/common/CommonDialog";
import { menuItemStyles, PaginationType } from "./Groups";
import UsersById, {
  dialogContextChildWrapperSx,
} from "../components/UsersByRankId";
import { useAuth } from "../context/AuthContext";
import { colors } from "../utils/constants";

//Images
import AddIcon from "../../src/assets/AddIcon.svg";
import NoRoleIcon from "../../src/assets/NoRoleBlue.svg";
import { containedButtonWithIconSx } from "./Users";
import { canCreateRoles } from "../utils/utils";

export interface Role {
  id: number;
  name: string;
  description?: string;
  permissions: {
    module: string;
    actions: string[];
  }[];
}

interface User {
  userId: number;
  fullname: string;
  email: string;
}

const RolesList: React.FC = () => {
  const { showAlert } = useAlert();
  const { user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<Role | null>(null);
  const [roleData, setRoleData] = useState<Role | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [roleUsersPaginationModel, setRoleUsersPaginationModel] =
    useState<PaginationType>({
      page: 0,
      pageSize: 10,
    });
  const [roleUsersSearchText, setRoleUsersSearchText] = useState<string>("");
  const [roleUsersTotalRowsCount, setRoleUsersTotalRowsCount] =
    useState<number>(0);
  const [roleUsersLoading, setRoleUsersLoading] = useState<boolean>(false);
  const isUserHasRoleCreationPermission = canCreateRoles(user!);

  const handleRoleUsersSearch = useCallback((value: string) => {
    setRoleUsersSearchText(value.trim());
    setRoleUsersPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const closeDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: Role
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setSelectedRole(row.name);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openDeleteDialog = () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    setDeleteDialog(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    const roleId = selectedRow.id;
    try {
      const response = await removeRole(roleId);
      if (response && response.status === 200) {
        closeDeleteDialog();
        showAlert("Role deleted successfully", "success");
        fetchRoles();
        setSelectedRow(null);
      }
    } catch (error: any) {
      console.error("Error deleting role:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete role";
      showAlert(errorMessage, "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleEdit = async () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    setMode("edit");
    const res = await getRoleById(selectedRow.id);
    setRoleData(res?.data);
    setOpen(true);
    handleMenuClose();
  };

  const handleAddRole = () => {
    setMode("add");
    setOpen(true);
  };

  const handleCloseRoleCreation = async () => {
    setOpen(false);
    setMode("add");
    if (mode === "add") {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    if (mode === "edit") await fetchRoles();
  };

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGridRoles({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        q: searchText.trim(),
      });
      if (response && response.data && response.data.roles) {
        const formattedRoles = response.data.roles.map((role: any) => ({
          id: role.roleId,
          name: role.roleName,
          description: role.description,
          permissions: role.permissions || [],
        }));
        const ids = formattedRoles.map((g: Role) => g.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error("Duplicate role IDs detected:", ids);
        }
        setRoles(formattedRoles);
        setTotalRowsCount(response.data.totalCount);
      } else {
        setRoles([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  const fetchUsersForRole = useCallback(async () => {
    setRoleUsersLoading(true);
    try {
      const response =
        selectedRow &&
        (await getUserForRole({
          role: selectedRow.id,
          page: roleUsersPaginationModel.page,
          size: roleUsersPaginationModel.pageSize,
          q: roleUsersSearchText.trim() || "",
        }));
      if (response && response.data && response.data.users) {
        setUsers(response.data.users);
        setRoleUsersTotalRowsCount(response.data.totalCount);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users for role:", error);
      setUsers([]);
    } finally {
      setRoleUsersLoading(false);
    }
  }, [roleUsersPaginationModel, roleUsersSearchText, selectedRow]);

  const handleCellClick = (params: any) => {
    if (params.field === "name" || params.field === "description") {
      setRoleUsersPaginationModel({ page: 0, pageSize: 10 });
      setRoleUsersSearchText("");
      setSelectedRow(params.row);
      setSelectedRole(params.row.name);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Role Name",
      width: 150,
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ cursor: "pointer" }}>{params.value || "--"}</Box>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ cursor: "pointer" }}>{params.value || "--"}</Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          {user &&
            user.permissions.some(
              (p) =>
                p.module === "Roles" &&
                (p.actions.includes("Edit") || p.actions.includes("Delete"))
            ) && (
              <IconButton
                onClick={(event) => handleMenuOpen(event, params.row)}
              >
                <MoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
            )}
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            sx={menuItemStyles}
          >
            {user?.permissions && (
              <>
                {user.permissions.some(
                  (p) => p.module === "Roles" && p.actions.includes("Edit")
                ) ? (
                  <MenuItem onClick={handleEdit}>Edit</MenuItem>
                ) : null}
              </>
            )}{" "}
            {user?.permissions && (
              <>
                {user.permissions.some(
                  (p) => p.module === "Roles" && p.actions.includes("Delete")
                ) ? (
                  <MenuItem onClick={openDeleteDialog}>Delete</MenuItem>
                ) : null}
              </>
            )}{" "}
          </Menu>
        </>
      ),
    },
  ];

  // Memoize rows to stabilize prop
  const memoizedRows = useMemo(() => roles, [roles]);
  const memoizedUserRows = useMemo(() => users, [users]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (selectedRow) {
      fetchUsersForRole();
    }
  }, [selectedRow, fetchUsersForRole]);
  const buttonsComponent = useCallback(
    () => (
      <>
        {isUserHasRoleCreationPermission ? (
          <Button
            variant="contained"
            onClick={handleAddRole}
            sx={containedButtonWithIconSx}
          >
            <img src={AddIcon} /> Add Role
          </Button>
        ) : null}
      </>
    ),
    [isUserHasRoleCreationPermission]
  );

  return (
    <Box sx={mainDivStyles}>
      {/* Sticky Header */}
      <Box sx={mainTitleStyles}>
        <Typography sx={titleStyles}>Role Management</Typography>
        <Typography sx={titleDescriptionStyles}>
          View and manage all roles in the system
        </Typography>
      </Box>

      {/* Scrollable Content  */}
      <Box sx={scrollContentStyles}>
        <Grid container spacing={2}>
          {/* Main content */}
          <Grid item xs={12} lg={5.8} sx={{ pt: 0 }}>
            <CommDataGrid
              sx={datagridPropStyles}
              section="Roles"
              title=""
              description="Select a role to view its users"
              onRowClick={undefined}
              onCellClick={handleCellClick}
              rows={memoizedRows}
              columns={columns}
              pageSize={10}
              renderButtons={buttonsComponent}
              setPaginationModel={setPaginationModel}
              paginationModel={paginationModel}
              totalRowsCount={totalRowsCount}
              searchText={searchText}
              handleSearch={handleSearch}
              loading={loading}
            />
          </Grid>
          <Grid item xs="auto">
            <Divider orientation="vertical" sx={{ height: "96%" }} />
          </Grid>

          {/* Users table */}
          <Grid item xs={12} lg={5.8}>
            {selectedRow ? (
              <UsersById
                key={users.length}
                section="roles"
                users={memoizedUserRows}
                rankName={selectedRole}
                fetchUsersByRank={fetchUsersForRole}
                setPaginationModel={setRoleUsersPaginationModel}
                paginationModel={roleUsersPaginationModel}
                totalRowsCount={roleUsersTotalRowsCount}
                searchText={roleUsersSearchText}
                handleSearch={handleRoleUsersSearch}
                selectedRow={selectedRow}
                loading={roleUsersLoading}
              />
            ) : (
              <Box sx={noRoleSx}>
                <img
                  src={NoRoleIcon}
                  alt="No Role"
                  style={{ height: "120px", width: "120px" }}
                />
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 400, color: "#000000" }}
                >
                  Select a role to view the users assigned to it.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      {open && (
        <CreateRoleModal
          open={open}
          handleClose={handleCloseRoleCreation}
          mode={mode}
          selectedRole={selectedRow}
          roleData={roleData}
        />
      )}
      {deleteDialog && (
        <CommonDialog
          title="Delete Role"
          cancelButton={closeDeleteDialog}
          actionButton={handleDelete}
          actionButtonTitle="Yes"
          cancelButtonTitle="Cancel"
        >
          <DialogContentText>
            <Box sx={dialogContextChildWrapperSx}>
              <Typography
                sx={{ color: "#000000", paddingBottom: 1, fontSize: "13px" }}
              >
                Are you sure you want to delete the role?
              </Typography>
              <Typography sx={{ fontSize: "12px" }}>
                {`The role will be deleted if you click on 'Yes'.`}
              </Typography>
            </Box>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default RolesList;

const addUserStyles: SxProps = {
  display: "flex",
  gap: 1,
  borderRadius: "8px",
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  textTransform: "inherit",
  ml: 1,
};

export const noRoleSx: SxProps = {
  minHeight: 400,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};

export const mainTitleStyles: SxProps = {
  position: "sticky",
  top: 0,
  zIndex: 3,
  py: 2,
  px: 3,
  pb: 0,
  flexShrink: 0,
};

export const scrollContentStyles: SxProps = {
  flex: 1,
  overflowY: "auto",
  position: "relative",
  p: 0,
  height: "calc(100vh - 120px)",
};

export const mainDivStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  height: "100vh",
};

export const datagridPropStyles: SxProps = {
  "& .MuiBox-root": {
    position: "sticky",
    pt: 0,
    zIndex: 2,
  },
};
